// src/pages/TaskExecutionPage.jsx

import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Stepper,
  Step,
  StepLabel,
  Button,
  TextField,
  Chip,
  Divider,
  Grid,
  IconButton,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Card,
  CardMedia,
  CardActions,
  LinearProgress,
  Container,
  Stack,
  CardContent,
  Snackbar,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import {
  ArrowBack,
  CameraAlt,
  Videocam,
  LocationOn,
  CheckCircle,
  Message,
  Schedule,
  Warning,
  Upload,
  Save,
  Description,
  Visibility,
  Edit,
  Refresh,
  GpsFixed,
  Help,
  ExpandMore
} from '@mui/icons-material';
import DashboardLayout from '../components/DashboardLayout';

export default function TaskExecutionPage() {
  const { taskId } = useParams();
  const navigate = useNavigate();
  
  // State management
  const [task, setTask] = useState(null);
  const [requirements, setRequirements] = useState([]);
  const [submission, setSubmission] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeStep, setActiveStep] = useState(0);
  const [proofText, setProofText] = useState('');
  const [uploadedImages, setUploadedImages] = useState([]);
  const [currentLocation, setCurrentLocation] = useState(null);
  const [messageDialogOpen, setMessageDialogOpen] = useState(false);
  const [newMessage, setNewMessage] = useState('');
  const [isUserAssigned, setIsUserAssigned] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({});
  const [isUploading, setIsUploading] = useState(false);
  const [showLocationInstructions, setShowLocationInstructions] = useState(false);
  const [locationError, setLocationError] = useState(null);
  const [notification, setNotification] = useState({
    open: false,
    message: ''
  });

  // Image slots state
  const [imageSlots, setImageSlots] = useState([]);
  const [maxSlots, setMaxSlots] = useState(6);

  const updateUploadProgress = (fileName, progress) => {
    setUploadProgress(prev => ({
      ...prev,
      [fileName]: progress
    }));
  };

  const hasDraftSubmission = () => {
    return submission && submission.status === 'Draft';
  };

  // Debug state
  const [debugInfo, setDebugInfo] = useState({
    token: localStorage.getItem('token'),
    user: JSON.parse(localStorage.getItem('user') || 'null'),
    apiCalls: [],
    tokenValid: false,
    tokenParts: 0
  });

  // Check token validity on component mount
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      const parts = token.split('.');
      setDebugInfo(prev => ({
        ...prev,
        tokenValid: parts.length === 3,
        tokenParts: parts.length
      }));
    }
  }, []);

  // Check if current user is assigned to this task
  useEffect(() => {
    if (task && debugInfo.user) {
      const assigned = task.assigned_to === debugInfo.user.id;
      setIsUserAssigned(assigned);
    }
  }, [task, debugInfo.user]);

  // Initialize image slots based on task requirements
  useEffect(() => {
    if (requirements.length > 0) {
      const photoRequirements = requirements.filter(req => 
        req.requirement_type === 'Photo' || req.requirement_type === 'Video'
      );
      
      const requiredSlots = Math.min(photoRequirements.length || 3, maxSlots);
      
      const initialSlots = Array(requiredSlots).fill(null).map((_, index) => ({
        id: `slot-${index}`,
        position: index,
        isRequired: index < (photoRequirements.length || 0),
        requirementText: photoRequirements[index]?.requirement_text || `Evidence ${index + 1}`,
        isFilled: false,
        imageUrl: null
      }));
      
      setImageSlots(initialSlots);
    } else {
      const defaultSlots = Array(3).fill(null).map((_, index) => ({
        id: `slot-${index}`,
        position: index,
        isRequired: index < 2,
        requirementText: `Evidence ${index + 1}`,
        isFilled: false,
        imageUrl: null
      }));
      setImageSlots(defaultSlots);
    }
  }, [requirements, maxSlots]);

  // Update image slots when uploadedImages changes
  useEffect(() => {
    if (uploadedImages.length > 0) {
      setImageSlots(prevSlots => {
        const updatedSlots = [...prevSlots];
        
        // Fill existing slots with uploaded images
        uploadedImages.forEach((url, index) => {
          if (index < updatedSlots.length) {
            updatedSlots[index] = {
              ...updatedSlots[index],
              isFilled: true,
              imageUrl: url
            };
          } else if (updatedSlots.length < maxSlots) {
            // Add new slot if we have more images than slots
            const newSlot = {
              id: `slot-${updatedSlots.length}`,
              position: updatedSlots.length,
              isRequired: false,
              requirementText: `Evidence ${updatedSlots.length + 1}`,
              isFilled: true,
              imageUrl: url
            };
            updatedSlots.push(newSlot);
          }
        });
        
        return updatedSlots;
      });
    }
  }, [uploadedImages, maxSlots]);

  // Add this useEffect to ensure submission state is consistent
  useEffect(() => {
    setUploadProgress({});
    setIsUploading(false);
  }, [submission]);

  // helper function to safely access submission ID
  const getSubmissionId = () => {
    if (!submission || !submission.id) {
      console.warn('No valid submission ID available');
      return null;
    }
    return submission.id;
  };

  // Fetch task data, requirements, and existing submission
  useEffect(() => {
    const fetchTaskData = async () => {
      try {
        setLoading(true);
        const token = localStorage.getItem('token');
        
        if (!token) {
          navigate('/login');
          return;
        }

        const apiCalls = [];

        // Fetch task details
        const taskResponse = await fetch(`https://propertyearn.net/api/api/tasks/${taskId}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        apiCalls.push({ endpoint: 'task-details', status: taskResponse.status });
        
        if (!taskResponse.ok) {
          if (taskResponse.status === 401) {
            localStorage.removeItem('token');
            localStorage.removeItem('user');
            navigate('/login');
            return;
          }
          throw new Error('Failed to fetch task details');
        }

        const taskData = await taskResponse.json();
        setTask(taskData.task || taskData);

        // Fetch task requirements
        const requirementsResponse = await fetch(`https://propertyearn.net/api/api/tasks/${taskId}/requirements`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        apiCalls.push({ endpoint: 'requirements', status: requirementsResponse.status });
        
        if (requirementsResponse.ok) {
          const requirementsData = await requirementsResponse.json();
          setRequirements(requirementsData.requirements || []);
        }

        // Fetch existing submission (if any)
        const submissionResponse = await fetch(`https://propertyearn.net/api/api/tasks/${taskId}/submission`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        apiCalls.push({ endpoint: 'submission', status: submissionResponse.status });
        
        if (submissionResponse.ok) {
          const submissionData = await submissionResponse.json();
          setSubmission(submissionData.submission);
          if (submissionData.submission) {
            setProofText(submissionData.submission.proof_text || '');
            
            // Safely set uploaded images - ensure it's always an array
            const proofImages = submissionData.submission.proof_image_urls;
            if (proofImages) {
              const imageArray = Array.isArray(proofImages) ? proofImages : [proofImages];
              setUploadedImages(imageArray);
            } else {
              setUploadedImages([]);
            }
          }
        }

        setDebugInfo(prev => ({
          ...prev,
          apiCalls: [...prev.apiCalls, ...apiCalls]
        }));
          
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          (position) => {
            setCurrentLocation({
              lat: position.coords.latitude,
              lon: position.coords.longitude
            });
            setLocationError(null); // Clear any previous errors
            setShowLocationInstructions(false); // Hide instructions if location granted
            setDebugInfo(prev => ({
              ...prev,
              apiCalls: [...prev.apiCalls, { endpoint: 'location', status: 'granted' }]
            }));
          },
          (err) => {
            console.warn('Location access denied:', err);
            
            // Set specific error message based on error code
            let errorMessage = 'Location access is required for task verification';
            let showInstructions = false;
            
            switch (err.code) {
              case err.PERMISSION_DENIED:
                errorMessage = 'Location access was denied. Please enable location permissions to complete this task.';
                showInstructions = true;
                break;
              case err.POSITION_UNAVAILABLE:
                errorMessage = 'Location information is unavailable. Please check your device settings.';
                showInstructions = true;
                break;
              case err.TIMEOUT:
                errorMessage = 'Location request timed out. Please try again.';
                break;
              default:
                errorMessage = 'An unknown error occurred while getting location.';
                break;
            }
            
            setLocationError(errorMessage);
            setShowLocationInstructions(showInstructions);
            setDebugInfo(prev => ({
              ...prev,
              apiCalls: [...prev.apiCalls, { endpoint: 'location', status: 'denied', error: err.message }]
            }));
          },
          {
            enableHighAccuracy: true,
            timeout: 10000, // 10 seconds timeout
            maximumAge: 300000 // 5 minutes cache
          }
        );
      } else {
        setLocationError('Geolocation is not supported by this browser. Please use a modern browser with location support.');
        setShowLocationInstructions(true);
        setDebugInfo(prev => ({
          ...prev,
          apiCalls: [...prev.apiCalls, { endpoint: 'location', status: 'not-supported' }]
        }));
      }
        
      } finally {
        setLoading(false);
      }
    };

    fetchTaskData();
  }, [taskId, navigate]);

  // Handle image upload - KEEPING ORIGINAL METHOD
 
const handleImageUpload = async (event) => {
  const files = Array.from(event.target.files);
  if (files.length === 0) return;

  try {
    const token = localStorage.getItem('token');
    setIsUploading(true);
    setError(null); // Clear previous errors
    
    let currentSubmissionId = submission?.id;

    // STEP 1: Create submission automatically if none exists
    if (!currentSubmissionId) {
      try {
        console.log('Creating new submission for file upload...');
        const draftResponse = await fetch(`https://propertyearn.net/api/api/tasks/${taskId}/submit`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            proof_text: proofText || 'Auto-created for image upload',
            proof_image_urls: [],
            status: 'Draft',
            performed_at: new Date().toISOString(),
            submission_lat: currentLocation?.lat,
            submission_lon: currentLocation?.lon
          })
        });

        if (!draftResponse.ok) {
          const errorData = await draftResponse.json();
          throw new Error(errorData.error || 'Failed to create submission');
        }

        const draftData = await draftResponse.json();
        currentSubmissionId = draftData.submission_id || draftData.submission?.id;
        
        if (!currentSubmissionId) {
          throw new Error('No submission ID returned from server');
        }
        
        console.log('Created submission with ID:', currentSubmissionId);
        setSubmission(draftData.submission || { 
          id: currentSubmissionId, 
          status: 'Draft',
          proof_text: proofText || 'Auto-created for image upload'
        });

      } catch (draftError) {
        console.error('Submission creation failed:', draftError);
        setError('Failed to prepare for upload: ' + draftError.message);
        setIsUploading(false);
        return;
      }
    }

    // STEP 2: Generate upload URLs with proper parameters
    console.log('Generating upload URLs for submission:', currentSubmissionId);
    const uploadResponse = await fetch('https://propertyearn.net/api/api/generate-upload-url', {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        task_id: parseInt(taskId),
        submission_id: parseInt(currentSubmissionId), // Ensure it's a number
        fileCount: files.length,
        fileNames: files.map(f => f.name)
      })
    });

    if (!uploadResponse.ok) {
      const errorData = await uploadResponse.json();
      throw new Error(errorData.error || 'Failed to get upload URLs');
    }
    
    const urlData = await uploadResponse.json();
    console.log('Upload URLs generated:', urlData);
    
    if (!urlData.uploadData || !Array.isArray(urlData.uploadData)) {
      throw new Error('Invalid response format from upload URL generation');
    }

    // STEP 3: Upload each file sequentially with progress tracking
    const newUploadedUrls = [];
    
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      const uploadInfo = urlData.uploadData[i];
      
      if (!uploadInfo || !uploadInfo.uploadUrl) {
        console.error('Missing upload info for file:', file.name);
        continue;
      }

      console.log(`Uploading file ${i + 1}/${files.length}:`, file.name);
      updateUploadProgress(file.name, 30);
      
      try {
        const uploadResponse = await fetch(uploadInfo.uploadUrl, {
          method: 'PUT',
          body: file,
          headers: { 
            'Authorization': `Bearer ${token}`,
            'Content-Type': file.type || 'image/jpeg', // Fallback content type
            'Content-Length': file.size // Add content length header
          }
        });

        if (!uploadResponse.ok) {
          const errorText = await uploadResponse.text();
          throw new Error(`Upload failed: ${errorText}`);
        }

        const uploadResult = await uploadResponse.json();
        console.log('Upload successful:', uploadResult);
        
        // Use the fileUrl from upload result or fallback to generated URL
        const finalFileUrl = uploadResult.fileUrl || uploadInfo.fileUrl;
        newUploadedUrls.push(finalFileUrl);
        updateUploadProgress(file.name, 100);
        
        // Update UI immediately for better UX
        setUploadedImages(prev => [...prev, finalFileUrl]);
        
      } catch (fileError) {
        console.error(`Failed to upload ${file.name}:`, fileError);
        setError(`Failed to upload ${file.name}: ${fileError.message}`);
        // Continue with other files instead of stopping completely
      }
    }

    // STEP 4: Update submission with all uploaded image URLs
    if (newUploadedUrls.length > 0) {
      try {
        console.log('Updating submission with new images...');
        const allImageUrls = [...uploadedImages, ...newUploadedUrls];
        
        const updateResponse = await fetch(`https://propertyearn.net/api/api/tasks/${taskId}/submit`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            proof_text: proofText || 'Task submission with images',
            proof_image_urls: allImageUrls,
            status: submission?.status || 'Draft',
            performed_at: new Date().toISOString(),
            submission_lat: currentLocation?.lat,
            submission_lon: currentLocation?.lon
          })
        });

        if (!updateResponse.ok) {
          console.warn('Failed to update submission with images, but files were uploaded');
        } else {
          const updatedSubmission = await updateResponse.json();
          setSubmission(updatedSubmission.submission || updatedSubmission);
        }
        
      } catch (updateError) {
        console.warn('Submission update failed, but files were uploaded:', updateError);
      }
    }

    // Clear progress after delay
    setTimeout(() => setUploadProgress({}), 2000);
    
  } catch (err) {
    console.error('Upload process error:', err);
    setError('Upload failed: ' + err.message);
  } finally {
    setIsUploading(false);
  }
};

  // Save submission as draft
  const saveDraft = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`https://propertyearn.net/api/api/tasks/${taskId}/submit`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          proof_text: proofText,
          proof_image_urls: uploadedImages,
          submission_lat: currentLocation?.lat,
          submission_lon: currentLocation?.lon,
          performed_at: new Date().toISOString(),
          status: 'Draft'
        })
      });

      if (!response.ok) throw new Error('Failed to save draft');
      
      const result = await response.json();
      setSubmission(result.submission);
      setNotification({
        open: true,
        message: 'Draft saved successfully!'
      });
    } catch (err) {
      setError('Failed to save draft: ' + err.message);
    }
  };

  // Submit for review
  const submitForReview = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`https://propertyearn.net/api/api/tasks/${taskId}/submit`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          proof_text: proofText,
          proof_image_urls: uploadedImages,
          submission_lat: currentLocation?.lat,
          submission_lon: currentLocation?.lon,
          performed_at: new Date().toISOString(),
          status: 'Pending Review'
        })
      });

      if (!response.ok) throw new Error('Failed to submit task');
      
      const result = await response.json();
      setSubmission(result.submission);
      setNotification({
        open: true,
        message: 'Task submitted for review successfully!'
      });
      setTimeout(() => navigate('/my-tasks'), 2000);
    } catch (err) {
      setError('Failed to submit task: ' + err.message);
    }
  };

  // Send message to task poster
  const sendMessage = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`https://propertyearn.net/api/api/tasks/${taskId}/messages`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          message_text: newMessage,
          recipient_id: task.created_by
        })
      });

      if (response.ok) {
        setNewMessage('');
        setMessageDialogOpen(false);
        setNotification({
          open: true,
          message: 'Message sent successfully!'
        });
      }
    } catch (err) {
      setError('Failed to send message: ' + err.message);
    }
  };

  // Location Instructions Dialog Component - FIXED VERSION
  const LocationInstructionsDialog = () => (
    <Dialog 
      open={showLocationInstructions} 
      onClose={() => {
        console.log('Closing location instructions');
        setShowLocationInstructions(false);
      }}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle sx={{ 
        bgcolor: 'primary.main', 
        color: 'primary.contrastText',
        display: 'flex',
        alignItems: 'center',
        gap: 1
      }}>
        <Help sx={{ fontSize: 28 }} />
        <Typography variant="h6" component="span">
          Location Help Guide
        </Typography>
      </DialogTitle>
      
      <DialogContent sx={{ p: 3 }}>
        <Typography variant="body1" paragraph sx={{ fontWeight: 'medium', mb: 2 }}>
          Follow these steps to enable location access for your device and browser:
        </Typography>

        {/* Mobile Instructions */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom sx={{ color: 'primary.main' }}>
            📱 Mobile Devices
          </Typography>
          
          <List dense>
            <ListItem>
              <ListItemText 
                primary="1. Open Settings → Privacy → Location Services"
                secondary="Ensure Location Services are turned on"
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="2. Find your browser in the app list"
                secondary="Chrome, Safari, or Firefox"
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="3. Set location access to 'While Using the App'"
                secondary="This allows temporary location access"
              />
            </ListItem>
          </List>
        </Box>

        {/* Desktop Instructions */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom sx={{ color: 'primary.main' }}>
            💻 Desktop Computers
          </Typography>
          
          <List dense>
            <ListItem>
              <ListItemText 
                primary="1. Look for location icon in address bar"
                secondary="Usually appears as 🎯 or 📍"
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="2. Click 'Allow' when prompted"
                secondary="Grant permission for this website"
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="3. Clear previous denials if needed"
                secondary="Click the icon and select 'Always allow'"
              />
            </ListItem>
          </List>
        </Box>

        {/* Quick Actions */}
        <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50' }}>
          <Typography variant="body2" sx={{ fontWeight: 'medium', mb: 1 }}>
            Quick Solutions:
          </Typography>
          <Button 
            variant="contained" 
            size="small"
            onClick={() => window.location.reload()}
            startIcon={<Refresh />}
            sx={{ mr: 1, mb: 1 }}
          >
            Refresh Page
          </Button>
          <Button 
            variant="outlined" 
            size="small"
            onClick={() => {
              setShowLocationInstructions(false);
              // Retry location access
              if (navigator.geolocation) {
                navigator.geolocation.getCurrentPosition(
                  (position) => {
                    setCurrentLocation({
                      lat: position.coords.latitude,
                      lon: position.coords.longitude
                    });
                    setLocationError(null);
                  },
                  (err) => {
                    setLocationError('Still unable to access location. Please check permissions.');
                  }
                );
              }
            }}
            startIcon={<GpsFixed />}
          >
            Retry Location
          </Button>
        </Paper>
      </DialogContent>
      
      <DialogActions sx={{ p: 2 }}>
        <Button 
          onClick={() => setShowLocationInstructions(false)}
          sx={{ mr: 1 }}
        >
          Close
        </Button>
        <Button 
          variant="contained" 
          onClick={() => window.location.reload()}
          startIcon={<Refresh />}
        >
          Refresh & Retry
        </Button>
      </DialogActions>
    </Dialog>
  );

  if (loading) {
    return (
      <DashboardLayout>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
          <CircularProgress />
        </Box>
      </DashboardLayout>
    );
  }

  if (error) {
    return (
      <DashboardLayout>
        <Alert severity="error" sx={{ m: 2 }}>
          {error}
        </Alert>
      </DashboardLayout>
    );
  }

  if (!task) {
    return (
      <DashboardLayout>
        <Alert severity="warning" sx={{ m: 2 }}>
          Task not found or you don't have permission to access it.
        </Alert>
      </DashboardLayout>
    );
  }

  const steps = ['Task Overview', 'Gather Proof', 'Review & Submit'];

  return (
    <DashboardLayout>
      {/* Header Section */}
      <Box sx={{ bgcolor: 'background.paper', py: 3, borderBottom: 1, borderColor: 'divider' }}>
        <Container maxWidth="lg">
          <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
            <IconButton onClick={() => navigate(-1)} sx={{ border: 1, borderColor: 'divider' }}>
              <ArrowBack />
            </IconButton>
            <Button 
              variant="outlined" 
              onClick={() => navigate(`/task/${taskId}`)}
              startIcon={<Visibility />}
            >
              View Details
            </Button>
            <Button 
              variant="outlined" 
              onClick={() => navigate('/my-tasks')}
            >
              My Tasks
            </Button>
          </Stack>

          <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
            <Box>
              <Typography variant="h4" component="h1" fontWeight="bold" gutterBottom>
                {task.title}
              </Typography>
              <Stack direction="row" spacing={2} alignItems="center">
                 <Typography variant="body1" color="text.secondary">
                  Complete this task to earn your reward
                </Typography>
                <Chip 
                  label={`£${task.reward}`} 
                  color="primary" 
                  variant="filled"
                  sx={{ fontWeight: 'bold', fontSize: '1.1rem' }}
                />
              </Stack>
            </Box>
            <Button
              variant="outlined"
              startIcon={<Message />}
              onClick={() => setMessageDialogOpen(true)}
              sx={{ minWidth: '160px' }}
            >
              Contact Poster
            </Button>
          </Stack>
        </Container>
      </Box>

      {/* Progress Stepper */}
      <Paper sx={{ p: 2, m: 2 }}>
        <Stepper activeStep={activeStep} alternativeLabel>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
      </Paper>

      {/* Main Content */}
      <Box sx={{ p: 3, maxWidth: '1000px', margin: '0 auto' }}>
        {activeStep === 0 && (
          <Paper sx={{ p: 3 }}>
            <Typography variant="h5" gutterBottom>
              📋 Task Requirements
            </Typography>
            <List>
              {requirements.map((req, index) => (
                <ListItem key={index}>
                  <ListItemIcon>
                    {req.requirement_type === 'Photo' && <CameraAlt />}
                    {req.requirement_type === 'Video' && <Videocam />}
                    {req.requirement_type === 'Location' && <LocationOn />}
                    {req.requirement_type === 'Text' && <CheckCircle />}
                  </ListItemIcon>
                  <ListItemText 
                    primary={req.requirement_text}
                    secondary={`Type: ${req.requirement_type} • Required: ${req.is_required ? 'Yes' : 'No'}`}
                  />
                </ListItem>
              ))}
            </List>

            <Divider sx={{ my: 3 }} />
            
              <Typography variant="h6" gutterBottom>
                📍 Location Verification
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Your current location will be captured when you submit this task to verify you are in United Kingdom.
              </Typography>

              {/* Enhanced Location Status Display */}
              <Box sx={{ mt: 2, p: 2, border: 1, borderColor: 'divider', borderRadius: 1 }}>
                {currentLocation ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CheckCircle color="success" />
                    <Typography variant="body1" color="success.main">
                      Location access granted ✅
                    </Typography>
                  </Box>
                ) : locationError ? (
                  <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <Warning color="warning" />
                      <Typography variant="body1" color="warning.main">
                        Location access required
                      </Typography>
                    </Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      {locationError}
                    </Typography>
                    <Button 
                      variant="outlined" 
                      size="small"
                      onClick={() => setShowLocationInstructions(true)}
                      startIcon={<LocationOn />}
                    >
                      Enable Location Access
                    </Button>
                  </Box>
                ) : (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CircularProgress size={20} />
                    <Typography variant="body2" color="text.secondary">
                      Requesting location access...
                    </Typography>
                  </Box>
                )}
              </Box>

              {/* Location Requirement Notice */}
              <Alert severity="info" sx={{ mt: 2 }}>
                <Typography variant="body2">
                  <strong>Why we need your location:</strong> This helps verify that you are resident of United Kingdom where this Task must be completed.
                </Typography>
              </Alert>

{/* Manual Retry Button - Show when location is denied */}
{locationError && !currentLocation && (
  <Box sx={{ mt: 1, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
    <Button 
      variant="outlined" 
      size="small"
      onClick={() => {
        // Retry location access
        if (navigator.geolocation) {
          navigator.geolocation.getCurrentPosition(
            (position) => {
              setCurrentLocation({
                lat: position.coords.latitude,
                lon: position.coords.longitude
              });
              setLocationError(null);
            },
            (err) => {
              setLocationError('Still unable to access location. Please check permissions.');
              setShowLocationInstructions(true);
            }
          );
        }
      }}
      startIcon={<GpsFixed />}
    >
      Retry Location
    </Button>
    
    <Button 
      variant="text" 
      size="small"
      onClick={() => {
        console.log('Need Help button clicked');
        setShowLocationInstructions(true);
      }}
      startIcon={<Help />}
      sx={{
        minWidth: 'auto',
        '&:hover': {
          backgroundColor: 'rgba(25, 118, 210, 0.04)',
        }
      }}
    >
      Need Help?
    </Button>
  </Box>
)}
            <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>      
              <Button 
                variant="contained" 
                onClick={() => setActiveStep(1)}
              >
                {hasDraftSubmission() ? 'Continue Task' : 'Start Task'}
              </Button>
            </Box>
          </Paper>
        )}

        {activeStep === 1 && (
          <Paper sx={{ p: 3 }}>
            <Typography variant="h5" gutterBottom>
              📸 Gather Proof Materials
            </Typography>
            
            {/* Text Proof */}
            <TextField
              fullWidth
              multiline
              rows={4}
              label="Task Description & Notes"
              value={proofText}
              onChange={(e) => setProofText(e.target.value)}
              placeholder="Describe what you did, any observations, or additional information..."
              sx={{ mb: 3 }}
            />

            {/* Image Upload Section with Slots */}
            <Typography variant="h6" gutterBottom>
              Upload Photos/Evidence
            </Typography>

            {/* Slot Status Summary */}
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, p: 1, bgcolor: 'grey.50', borderRadius: 1 }}>
              <Typography variant="body2" sx={{ mr: 2 }}>
                <strong>Progress:</strong> {imageSlots.filter(slot => slot.isFilled).length} of {imageSlots.length} slots filled
              </Typography>
              {imageSlots.some(slot => slot.isRequired && !slot.isFilled) && (
                <Chip 
                  icon={<Warning />} 
                  label="Required slots missing" 
                  color="warning" 
                  size="small" 
                />
              )}
            </Box>

            {/* Image Slots Grid */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
              {imageSlots.map((slot, index) => (
                <Grid item xs={12} sm={6} md={4} key={slot.id}>
                  <Card 
                    sx={{ 
                      height: 200,
                      border: slot.isRequired && !slot.isFilled ? '2px dashed #f44336' : '2px dashed #e0e0e0',
                      backgroundColor: slot.isFilled ? 'transparent' : 'grey.50',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      position: 'relative',
                      overflow: 'hidden'
                    }}
                  >
                    {slot.isFilled ? (
                      // Filled Slot - Show Image
                      <>
                        <CardMedia
                          component="img"
                          height="140"
                          image={slot.imageUrl}
                          alt={`Proof image ${index + 1}`}
                          sx={{ objectFit: 'cover' }}
                        />
                        <CardActions sx={{ width: '100%', justifyContent: 'center', p: 1 }}>
                       <Button 
  size="small" 
  color="error"
  onClick={(e) => {
    e.stopPropagation(); // Prevent event bubbling
    e.preventDefault(); // Prevent default behavior
    
    console.log('Remove button clicked for image:', slot.imageUrl);
    
    // Use immediate state update for better UX - update BOTH states
    const updatedImages = uploadedImages.filter(url => url !== slot.imageUrl);
    setUploadedImages(updatedImages);
    
    // Also update imageSlots to reflect the removal in UI
    setImageSlots(prevSlots => 
      prevSlots.map(s => 
        s.imageUrl === slot.imageUrl 
          ? { ...s, isFilled: false, imageUrl: null }
          : s
      )
    );
    
    // Update server asynchronously without blocking UI
    const updateServer = async () => {
      try {
        const token = localStorage.getItem('token');
        
        const response = await fetch(`https://propertyearn.net/api/api/tasks/${taskId}/submit`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            proof_text: proofText,
            proof_image_urls: updatedImages,
            status: submission?.status || 'Draft',
            performed_at: new Date().toISOString()
          })
        });
        
        if (!response.ok) {
          // Revert if server update fails - revert BOTH states
          console.warn('Server update failed, reverting UI');
          setUploadedImages(prev => [...prev, slot.imageUrl]);
          setImageSlots(prevSlots => 
            prevSlots.map(s => 
              s.id === slot.id 
                ? { ...s, isFilled: true, imageUrl: slot.imageUrl }
                : s
            )
          );
          throw new Error('Failed to remove image from submission');
        }
        
        const result = await response.json();
        setSubmission(result.submission || result);
        console.log('Image successfully removed from server');
        
      } catch (err) {
        console.error('Remove image error:', err);
        setError('Failed to remove image: ' + err.message);
      }
    };
    
    // Start server update without awaiting
    updateServer();
  }}
  sx={{
    minWidth: '70px',
    zIndex: 10,
    position: 'relative'
  }}
>
  Remove
                      </Button>

                          <Button 
                            size="small" 
                            onClick={() => window.open(slot.imageUrl, '_blank')}
                          >
                            View Full
                          </Button>
                        </CardActions>
                      </>
                    ) : (
                      // Empty Slot - Show Upload Prompt
                      <>
                        <input
                          accept="image/*"
                          style={{ display: 'none' }}
                          id={`image-slot-upload-${index}`}
                          type="file"
                          onChange={handleImageUpload}
                          disabled={isUploading}
                        />
                        <label htmlFor={`image-slot-upload-${index}`} style={{ width: '100%', height: '100%' }}>
                          <Box
                            sx={{
                              width: '100%',
                              height: '100%',
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: 'center',
                              justifyContent: 'center',
                              cursor: isUploading ? 'default' : 'pointer',
                              '&:hover': {
                                backgroundColor: isUploading ? 'grey.50' : 'action.hover',
                              }
                            }}
                          >
                            <CameraAlt sx={{ fontSize: 40, color: 'text.secondary', mb: 1 }} />
                            <Typography variant="body2" align="center" color="text.secondary">
                              {slot.requirementText}
                            </Typography>
                            {slot.isRequired && (
                              <Chip 
                                label="Required" 
                                size="small" 
                                color="error" 
                                sx={{ mt: 1, height: 20, fontSize: '0.7rem' }} 
                              />
                            )}
                          </Box>
                        </label>
                      </>
                    )}
                  </Card>
                </Grid>
              ))}
            </Grid>

            {/* Add More Slots Button */}
            {imageSlots.length < maxSlots && (
              <Box sx={{ textAlign: 'center', mb: 2 }}>
                <Button
                  variant="outlined"
                  onClick={() => {
                    const newSlot = {
                      id: `slot-${imageSlots.length}`,
                      position: imageSlots.length,
                      isRequired: false,
                      requirementText: `Additional Evidence ${imageSlots.length + 1}`,
                      isFilled: false,
                      imageUrl: null
                    };
                    setImageSlots(prev => [...prev, newSlot]);
                  }}
                  startIcon={<Upload />}
                >
                  Add More Evidence Slots
                </Button>
              </Box>
            )}

            {/* Upload Progress */}
            {isUploading && (
              <Box sx={{ mt: 1 }}>
                {Object.entries(uploadProgress).map(([fileName, progress]) => (
                  <Box key={fileName} sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <Typography variant="body2" sx={{ mr: 1, minWidth: 120 }}>
                      {fileName.length > 15 ? fileName.substring(0, 15) + '...' : fileName}
                    </Typography>
                    <Box sx={{ width: '100%', mr: 1 }}>
                      <LinearProgress variant="determinate" value={progress} />
                    </Box>
                    <Typography variant="body2">{progress}%</Typography>
                  </Box>
                ))}
              </Box>
            )}

            <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
              <Button variant="outlined" onClick={() => setActiveStep(0)}>
                Back
              </Button>
              <Button variant="contained" onClick={() => setActiveStep(2)}>
                Review Submission
              </Button>
              <Button 
                variant="outlined" 
                startIcon={<Save />} 
                onClick={saveDraft}
              >
                {submission ? 'Update Draft' : 'Save Draft'}
              </Button>
            </Box>
          </Paper>
        )}

        {activeStep === 2 && (
          <Paper sx={{ p: 3 }}>
            <Typography variant="h5" gutterBottom>
              👁️ Review Your Submission
            </Typography>
            
            {/* Proof Text Review */}
            {proofText && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="h6">Description:</Typography>
                <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50' }}>
                  {proofText}
                </Paper>
              </Box>
            )}

            {/* Images Review with Slots */}
            {imageSlots.some(slot => slot.isFilled) && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="h6">Uploaded Evidence:</Typography>
                <Grid container spacing={1} sx={{ mt: 1 }}>
                  {imageSlots.filter(slot => slot.isFilled).map((slot, index) => (
                    <Grid item xs={4} key={slot.id}>
                      <Box sx={{ position: 'relative' }}>
                        <img
                          src={slot.imageUrl}
                          alt={`Proof ${index + 1}`}
                          style={{ width: '100%', height: '100px', objectFit: 'cover', borderRadius: '4px' }}
                        />
                        {slot.isRequired && (
                          <Chip 
                            label="Required" 
                            size="small" 
                            color="error" 
                            sx={{ 
                              position: 'absolute', 
                              top: 4, 
                              right: 4,
                              height: 20,
                              fontSize: '0.6rem'
                            }} 
                          />
                        )}
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            )}

            {/* Location Verification */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="h6">Location Verification:</Typography>
              {currentLocation ? (
                <Chip icon={<LocationOn />} label="Location will be verified on submission" color="success" />
              ) : (
                <Chip icon={<Warning />} label="Location access required for verification" color="error" />
              )}
            </Box>

            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button variant="outlined" onClick={() => setActiveStep(1)}>
                Edit Submission
              </Button>
              <Button 
                variant="contained" 
                onClick={submitForReview}
                disabled={!currentLocation || imageSlots.filter(slot => slot.isRequired && !slot.isFilled).length > 0}
              >
                Submit for Review
              </Button>
            </Box>
          </Paper>
        )}
      </Box>

      {/* Message Dialog */}
      <Dialog open={messageDialogOpen} onClose={() => setMessageDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Message Task Poster</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            multiline
            rows={4}
            fullWidth
            variant="outlined"
            label="Your message"
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMessageDialogOpen(false)}>Cancel</Button>
          <Button 
            onClick={() => {
              setNotification({
                open: true,
                message: 'Message successfully sent. Contact us via the Help Center page for any other inquiries.'
              });
              setNewMessage('');
              setMessageDialogOpen(false);
            }} 
            variant="contained" 
            disabled={!newMessage.trim()}
          >
            Send Message
          </Button>
        </DialogActions>
      </Dialog>

      {/* Location Instructions Dialog - ADDED IN MAIN RENDER */}
      <LocationInstructionsDialog />

      {/* Green-Themed Square Notification */}
      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={() => setNotification({ open: false, message: '' })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        sx={{
          '& .MuiSnackbar-root': {
            alignItems: 'center',
            justifyContent: 'center',
          }
        }}
      >
        <Paper
          sx={{
            width: '320px',
            height: '320px',
            borderRadius: 3,
            background: 'linear-gradient(135deg, #4CAF50 0%, #2E7D32 50%, #1B5E20 100%)',
            position: 'relative',
            overflow: 'hidden',
            boxShadow: '0 20px 40px rgba(76, 175, 80, 0.3)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'space-between',
            p: 3,
            border: '1px solid rgba(255,255,255,0.2)',
            backdropFilter: 'blur(10px)'
          }}
        >
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: `
                radial-gradient(circle at 20% 20%, rgba(76, 175, 80, 0.3) 0%, transparent 50%),
                radial-gradient(circle at 80% 80%, rgba(46, 125, 50, 0.3) 0%, transparent 50%),
                radial-gradient(circle at 40% 60%, rgba(27, 94, 32, 0.2) 0%, transparent 50%)
              `,
              zIndex: 0
            }}
          />
          
          <IconButton
            onClick={() => setNotification({ open: false, message: '' })}
            sx={{
              position: 'absolute',
              top: 12,
              right: 12,
              backgroundColor: 'rgba(255,255,255,0.2)',
              backdropFilter: 'blur(10px)',
              color: 'white',
              width: 32,
              height: 32,
              zIndex: 2,
              border: '1px solid rgba(255,255,255,0.3)',
              '&:hover': {
                backgroundColor: 'rgba(255,255,255,0.3)',
                transform: 'scale(1.1)'
              },
              transition: 'all 0.2s ease-in-out'
            }}
          >
            ×
          </IconButton>

          <Box sx={{ 
            position: 'relative', 
            zIndex: 1,
            mt: 4,
            textAlign: 'center'
          }}>
            <Box
              sx={{
                width: 80,
                height: 80,
                borderRadius: '50%',
                background: 'linear-gradient(135deg, rgba(255,255,255,0.95) 0%, rgba(255,255,255,0.8) 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 20px',
                boxShadow: '0 8px 24px rgba(27, 94, 32, 0.4)',
                animation: 'pulse 2s ease-in-out infinite',
                '@keyframes pulse': {
                  '0%, 100%': { transform: 'scale(1)' },
                  '50%': { transform: 'scale(1.05)' }
                },
                border: '2px solid rgba(76, 175, 80, 0.3)'
              }}
            >
              <CheckCircle 
                sx={{ 
                  fontSize: 42,
                  color: '#2E7D32',
                  filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.2))'
                }} 
              />
            </Box>
            
            <Typography 
              variant="h5" 
              component="div"
              sx={{
                color: 'white',
                fontWeight: 'bold',
                fontSize: '1.6rem',
                textShadow: '0 2px 8px rgba(27, 94, 32, 0.6)',
                mb: 1,
                letterSpacing: '0.5px'
              }}
            >
              Success!
            </Typography>
            
            <Typography 
              variant="body2"
              sx={{
                color: 'rgba(255,255,255,0.9)',
                fontWeight: 500,
                textShadow: '0 1px 2px rgba(0,0,0,0.3)'
              }}
            >
              Your message has been delivered
            </Typography>
          </Box>

          <Box sx={{ 
            position: 'relative', 
            zIndex: 1,
            textAlign: 'center',
            flexGrow: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            px: 2
          }}>
            <Typography 
              variant="body1"
              sx={{
                color: 'white',
                fontWeight: 500,
                lineHeight: 1.5,
                textShadow: '0 1px 3px rgba(0,0,0,0.4)',
                fontSize: '0.95rem',
                backgroundColor: 'rgba(255,255,255,0.1)',
                borderRadius: 2,
                px: 2,
                py: 1,
                backdropFilter: 'blur(5px)'
              }}
            >
              {notification.message}
            </Typography>
          </Box>

          <Box sx={{ 
            position: 'relative', 
            zIndex: 1,
            width: '100%',
            textAlign: 'center'
          }}>
            <Button
              onClick={() => setNotification({ open: false, message: '' })}
              variant="contained"
              sx={{
                backgroundColor: 'rgba(255,255,255,0.95)',
                color: '#2E7D32',
                fontWeight: 'bold',
                borderRadius: 2,
                px: 4,
                py: 1.2,
                textTransform: 'none',
                fontSize: '1rem',
                boxShadow: '0 4px 12px rgba(27, 94, 32, 0.4)',
                border: '1px solid rgba(255,255,255,0.5)',
                '&:hover': {
                  backgroundColor: 'rgba(255,255,255,1)',
                  transform: 'translateY(-2px)',
                  boxShadow: '0 6px 20px rgba(27, 94, 32, 0.6)'
                },
                transition: 'all 0.3s ease-in-out'
              }}
            >
              Okay!
            </Button>
            
            <Box 
              sx={{
                width: '100%',
                height: '4px',
                backgroundColor: 'rgba(255,255,255,0.2)',
                borderRadius: 2,
                mt: 2,
                overflow: 'hidden'
              }}
            >
              <Box
                sx={{
                  width: '100%',
                  height: '100%',
                  backgroundColor: 'rgba(255,255,255,0.9)',
                  borderRadius: 2,
                  animation: 'shrink 6s linear forwards',
                  '@keyframes shrink': {
                    '0%': { transform: 'scaleX(1)' },
                    '100%': { transform: 'scaleX(0)' }
                  },
                  boxShadow: '0 0 8px rgba(255,255,255,0.5)'
                }}
              />
            </Box>
          </Box>
        </Paper>
      </Snackbar>
    </DashboardLayout>
  );
}